import { useState } from "react";
import { Bot, Pause, Play, Power } from "lucide-react";
import { toast } from "sonner";
import { useApiClient } from "@/lib/apiClient";

/**
 * Controllo condiviso stato AI (Fase 10B): 3 stati soli — active | paused | disabled.
 * Usato in Lead Inbox e Contact Drawer. Nessuna business logic duplicata.
 */
const META = {
  active: { dot: "🟢", label: "AI attiva", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  paused: { dot: "🟡", label: "AI in pausa", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  disabled: { dot: "⚪", label: "AI disattivata", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export function AiStatusControl({ caseId, status, onChanged, canActivate = true }) {
  const api = useApiClient();
  const [busy, setBusy] = useState(false);
  const st = META[status] ? status : "disabled";
  const meta = META[st];

  const setStatus = async (next) => {
    if (!caseId || busy) return;
    setBusy(true);
    try {
      await api.patch(`/agency/cases/${caseId}`, { ai_status: next });
      onChanged?.(next);
      toast.success(META[next]?.label ? `${META[next].label}` : "Aggiornato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione non riuscita");
    } finally { setBusy(false); }
  };

  const btn = "inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border font-bold disabled:opacity-50";

  return (
    <div className="inline-flex items-center gap-1.5 flex-wrap" data-testid="ai-status-control">
      <span data-testid="ai-status-badge"
        className={`inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border font-bold ${meta.cls}`}>
        <Bot className="h-3.5 w-3.5" /> {meta.label}
      </span>
      {st === "active" && (
        <>
          <button data-testid="ai-action-pause" disabled={busy} onClick={() => setStatus("paused")}
            className={`${btn} bg-white border-[color:var(--sp-border)]`}><Pause className="h-3.5 w-3.5" /> Metti in pausa</button>
          <button data-testid="ai-action-disable" disabled={busy} onClick={() => setStatus("disabled")}
            className={`${btn} bg-white border-[color:var(--sp-border)]`}><Power className="h-3.5 w-3.5" /> Disattiva</button>
        </>
      )}
      {st === "paused" && (
        <>
          <button data-testid="ai-action-reactivate" disabled={busy} onClick={() => setStatus("active")}
            className={`${btn} bg-emerald-50 border-emerald-200 text-emerald-700`}><Play className="h-3.5 w-3.5" /> Riattiva AI</button>
          <button data-testid="ai-action-disable" disabled={busy} onClick={() => setStatus("disabled")}
            className={`${btn} bg-white border-[color:var(--sp-border)]`}><Power className="h-3.5 w-3.5" /> Disattiva</button>
        </>
      )}
      {st === "disabled" && canActivate && (
        <button data-testid="ai-action-activate" disabled={busy} onClick={() => setStatus("active")}
          className={`${btn} bg-emerald-50 border-emerald-200 text-emerald-700`}><Play className="h-3.5 w-3.5" /> Attiva AI</button>
      )}
    </div>
  );
}
